import { lazy, Suspense } from 'react';
import { BrowserRouter, Redirect } from 'react-router-dom';
import { Route, Switch } from 'react-router';
import { RedirectLoginOptions, withAuthenticationRequired } from '@auth0/auth0-react';

// Context providers
import { AuthProvider } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AlertProvider } from './contexts/AlertContext';
import DeviceContextProvider from './contexts/DeviceContextProvider';
import { ThemeProvider } from './contexts/ThemeContext';

// Components
import { Header } from './components/Header';
import { Contents } from './components/Contents';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Loading } from './components/Loading';
import { NotificationPanel } from './components/NotificationPanel';
import { PrivateRoute } from './PrivateRoute';

// Lazy-loaded page components
const routeFallback = <Loading />;
const DashboardPage = lazy(
  () => import(/* webpackChunkName: "dashboard" */ './pages/Dashboard/DashboardPage'),
);
const ProfilePage = lazy(
  () => import(/* webpackChunkName: "profile" */ './pages/Profile/ProfilePage'),
);
const SettingsPage = lazy(
  () => import(/* webpackChunkName: "settings" */ './pages/Settings/SettingsPage'),
);
const ResidentsPage = lazy(
  () => import(/* webpackChunkName: "residents" */ './pages/Residents/ResidentsPage'),
);
const AlertsPage = lazy(() => import(/* webpackChunkName: "alerts" */ './pages/Alerts/AlertsPage'));
const NoPage = lazy(() => import(/* webpackChunkName: "pages" */ './pages/NoPage'));
const LoginPage = lazy(() => import(/* webpackChunkName: "pages" */ './pages/LoginPage'));
const ForbiddenPage = lazy(() => import(/* webpackChunkName: "pages" */ './pages/ForbiddenPage'));
const InvitationConfirmationPage = lazy(
  () => import(/* webpackChunkName: "pages" */ './pages/Invitation/InvitationConfirmationPage'),
);
const ConsentPage = lazy(
  () => import(/* webpackChunkName: "pages" */ './pages/Consent/ConsentPage'),
);
const LicenseAgreementPage = lazy(
  () => import(/* webpackChunkName: "pages" */ './pages/License/LicenseAgreementPage'),
);
const ConfirmEmailPage = lazy(
  () => import(/* webpackChunkName: "pages" */ './pages/ConfirmEmail/ConfirmEmailPage'),
);
const PlatformRedirectPage = lazy(
  () => import(/* webpackChunkName: "pages" */ './pages/PlatformRedirect/PlatformRedirectPage'),
);

/**
 * Main application content with routing
 */
const AppContents = () => (
  <DataProvider>
    <NotificationProvider>
      <AlertProvider>
        <DeviceContextProvider>
          <BrowserRouter>
            <ThemeProvider defaultTheme="lacasa">
              <Header top={0} />
              <NotificationPanel />
              <Contents>
                <Suspense fallback={routeFallback}>
                  <Switch>
                    <PrivateRoute path="/" exact component={DashboardPage} />
                    <PrivateRoute path="/residents" exact component={ResidentsPage} />
                    <PrivateRoute path="/alerts" exact component={AlertsPage} />
                    <PrivateRoute path="/profile" exact component={ProfilePage} />
                    <PrivateRoute path="/settings" exact component={SettingsPage} />
                    <Route path="/auth" exact component={LoginPage} />
                    <Route path="/403" exact component={ForbiddenPage} />
                    <Route path="/404" exact component={NoPage} />
                    <Redirect to="/" />
                  </Switch>
                </Suspense>
              </Contents>
            </ThemeProvider>
          </BrowserRouter>
        </DeviceContextProvider>
      </AlertProvider>
    </NotificationProvider>
  </DataProvider>
);

/**
 * Web application structure with authentication
 */
const WebStruct = ({ loginOptions }: { loginOptions: RedirectLoginOptions }) => {
  const AppLoader = withAuthenticationRequired(() => <AppContents />, {
    loginOptions,
    onRedirecting: () => <div className="login-wallpaper" />,
  });

  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppLoader />
      </AuthProvider>
    </ErrorBoundary>
  );
};

/**
 * Dialog structure for invitation and consent pages
 */
const DialogStruct = () => (
  <ErrorBoundary>
    <BrowserRouter>
      <Suspense fallback={routeFallback}>
        <Switch>
          <Route path="/invitations/:id" component={InvitationConfirmationPage} />
          <Route path="/:deviceId/consent/:consentId" component={ConsentPage} />
          <Route path="/license" component={LicenseAgreementPage} />
          <Route path="/confirm-email" component={ConfirmEmailPage} />
          <Route path="/platform-redirect" component={PlatformRedirectPage} />
          <Route path="/change-password" component={PlatformRedirectPage} />
        </Switch>
      </Suspense>
    </BrowserRouter>
  </ErrorBoundary>
);

/**
 * Main App component
 */
function App() {
  const href = window.location.href;
  const params = new URLSearchParams(window.location.search);
  const loginOptions = { screen_hint: params.get('screen_hint') || '' };

  if (
    href.includes('/invitations/') ||
    href.includes('/consent/') ||
    href.includes('/license') ||
    href.includes('/confirm-email') ||
    href.includes('/platform-redirect') ||
    href.includes('/change-password')
  ) {
    return <DialogStruct />;
  }

  return <WebStruct loginOptions={loginOptions} />;
}

export default App;
